import Swal from 'sweetalert2';
import {
  IPortalPropertyPhotoAsset,
  IPortalPropertyPhotoObj,
  IPortalPropertyTagObj,
} from '../../../interfaces/asset';
import { showToast, ToastType } from '../../../utils/toast';

const denormaliseData = (normalised: NormalisedData) => {
  const sortedProperties = Object.values(normalised.properties).sort(
    (p1, p2) => p1.index - p2.index
  );

  return sortedProperties.map((property) => {
    const Property = { ...property };

    // the following were added during the normalisation process to store
    // IDs and positional information. No longer needed when denormalised.
    delete Property.index;
    delete Property.tags;
    delete Property.photos;

    const Tag = property.tags.map((tagId) => {
      const tag = normalised.tags[tagId];
      const Photo = (tag?.photos || []).map((photoId) => normalised.photos[photoId]);
      return { ...tag, Photo };
    });

    const Photo = property.photos.map((photoId) => normalised.photos[photoId]);

    return {
      NHID: property.NHID,
      Property,
      Tag,
      Photo,
    };
  });
};

type NormalisedData = {
  tags: Record<string, IPortalPropertyTagObj & { photos: number[] }>;
  photos: Record<string, IPortalPropertyPhotoObj>;
  properties: Record<
    string,
    IPortalPropertyPhotoAsset['Property'] & {
      index: number;
      tags: number[];
      photos: number[];
    }
  >;
};

function normaliseData(data: IPortalPropertyPhotoAsset[]) {
  const normalised: NormalisedData = { tags: {}, photos: {}, properties: {} };

  data.forEach((entry: IPortalPropertyPhotoAsset, index: number) => {
    if (entry.Tag) {
      entry.Tag.forEach((tag) => {
        normalised.tags[tag.AssetTagID] = { ...tag, photos: [] };
        if (tag.Photo) {
          tag.Photo.forEach((photo: IPortalPropertyPhotoObj) => {
            normalised.photos[photo.AssetID] = { ...photo };
            normalised.tags[tag.AssetTagID].photos.push(photo.AssetID);
          });
        }
      });
    }

    if (entry.Photo) {
      entry.Photo.forEach((photo: IPortalPropertyPhotoObj) => {
        if (!normalised.photos[photo.AssetID]) {
          normalised.photos[photo.AssetID] = { ...photo };
        }
      });
    }

    if (entry.Property) {
      if (!normalised.properties[entry.Property.NHID]) {
        normalised.properties[entry.Property.NHID] = {
          ...entry.Property,
          index,
          tags: entry.Tag.map((tag: IPortalPropertyTagObj) => tag.AssetTagID),
          photos: entry.Photo.map(
            (photo: IPortalPropertyPhotoObj) => photo.AssetID
          ),
        };
      }
    }
  });
  return normalised;
}

const showErrorDialog = (message: string) => {
  Swal.fire({
    title: 'Error',
    text: message,
    icon: 'warning',
    confirmButtonText: 'Okay',
    allowOutsideClick: false,
    buttonsStyling: false,
    customClass: {
      confirmButton: 'btn btn-success m-1',
    },
  });
};

const showConfirmationDialog = ({title, message}: {title: string, message: string}) => {
  return Swal.fire({
    title,
    text: message,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes',
    allowOutsideClick: false,
    buttonsStyling: false,
    customClass: {
      confirmButton: 'btn btn-success m-1',
      denyButton: 'btn btn-error m-1',
      cancelButton: 'btn btn-error m-1',
    },
  });
};

const showChangesMadeToast = () => {
  showToast('Updates will take up to 1 hour to show on nursinghomes.com', ToastType.Info);
};

export { normaliseData, denormaliseData, showErrorDialog, showConfirmationDialog, showChangesMadeToast };
